// @flow

// Libs
import React, { Component } from 'react'
import _isEmpty from 'lodash/isEmpty'
import _size from 'lodash/size'
import _first from 'lodash/first'
import _find from 'lodash/find'
import { Link, withRouter } from 'react-router-dom'
import { FormattedMessage } from 'react-intl'

// Messages
import { messages as recommendation_messages } from 'hfty-recommendations'

// Components
import DataTable from './DataTable'
import Add from 'fc-icons/src/Add'
import Pencil from 'fc-icons/src/Pencil'
import Delete from 'fc-icons/src/Delete'
import DeleteRecommendations from './DeleteRecommendations'
import Button from '@react-mdc/button'

// Container
import recommendationsManagerContainer from '../containers/RecommendationsManager'

// Types
import type { Asset } from 'fc-model-assets/types'
import type { SoilJob } from 'hfty-model-soil-jobs/types'
import type { RecommendationOperationsById } from 'hfty-operator-recommendations/types'
import type { Crop } from 'fc-model-crops/types'
import type { Fertilizer } from 'fc-model-fertilizers/types'

// Constants
import {
  PROP_SOIL_JOB_ID,
  PROP_FIELD,
  PROP_FARM,
  PROP_STATUS,
  RESULTS_POSTED,
} from 'hfty-model-soil-jobs'
import {
  PROP_ASSET_ID,
  PROP_LABEL,
} from 'fc-model-assets'

// Styles
import { lightGrey, grey, dark } from 'hfty-styles/src/colors'
const styles = {
  container: {
    flex: '1 1 auto',
    display: 'flex',
    flexDirection: 'column',
  },
  panel: {
    width: 300,
    backgroundColor: '#333',
    color: '#fff',
    boxShadow: '-2 0 2px rgba(0, 0, 0, 0.2)',
  },
  tableButton: {
    cursor: 'pointer',
    padding: '2px 7px',
  },
  header: {
    padding: 10,
    backgroundColor: lightGrey(0.8),
    display: 'flex',
    alignItems: 'center',
  },
  titleWrapper: {
    margin: '0 20px',
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
  },
  heading: {
    fontSize: '1.4em',
    color: grey(),
  },
  headingItem: {
    fontWeight: 400,
    fontSize: '0.9em',
    color: grey(0.6),
    marginLeft: 15,
    marginTop: 5,
  },
  itemTitle: {
    fontWeight: 500,
    marginRight: 5,
  },
  notAvailable: {
    margin: '40px auto',
    padding: '0 10px',
    fontSize: '1.4em',
    color: grey(0.9),
  },
  datatableContainer: {
    flex: '1 1 auto',
    display: 'flex',
    flexDirection: 'column',
  },
}

// Props type
type Props = {
  match: Object,
  history: Object,
  selectedIds: Array<number>,
  farms: Array<Asset>,
  fields: Array<Asset>,
  jobs: Array<SoilJob>,
  crops: Array<Crop>,
  currentUserUnits: string,
  operations: RecommendationOperationsById,
  token: string,
  routerQuery: string,
  fertilizers: Array<Fertilizer>,
  actions: {
    removeRecommendation: Function,
    onCancelOperations: Function,
    onSubmitOperations: Function,
    fetchHeftyCrops: Function,
    fetchFertilizers: Function,
  }
}

// Exports
export class RecommendationsManager extends Component {
  props: Props
  constructor () {
    super()
    this.onAdd = this.onAdd.bind(this)
    this.onEdit = this.onEdit.bind(this)
    this.onDelete = this.onDelete.bind(this)
  }

  componentDidMount () {
    this.props.actions.fetchHeftyCrops()

    if (_isEmpty(this.props.fertilizers)) {
      this.props.actions.fetchFertilizers()
    }
  }

  canEdit (): boolean {
    return this.props.selectedIds.length === 1 && !!this.getField()
  }

  canDelete (): boolean {
    return this.props.selectedIds.length > 0 && _size(this.props.crops)
  }

  canAdd (): boolean {
    return !!this.getField()
  }

  onAdd () {
    const { history } = this.props
    const job = this.getJob()
    if (job && this.canAdd()) {
      const fieldId = job[PROP_FIELD]
      fieldId && history.push(`/recommendations/${job[PROP_SOIL_JOB_ID]}/${fieldId}/add`)
    }
  }

  onEdit () {
    const { selectedIds, history } = this.props
    const job = this.getJob()
    if (job && this.canEdit()) {
      const fieldId = job[PROP_FIELD]
      fieldId && history.push(`/recommendations/${job[PROP_SOIL_JOB_ID]}/${fieldId}/edit/${_first(selectedIds)}`)
    }
  }

  onDelete () {
    this.canDelete() && this.props.actions.removeRecommendation()
  }

  getJob (): Asset {
    const { jobs, match } = this.props
    return _find(jobs, (job: SoilJob): boolean => {
      return job[PROP_SOIL_JOB_ID] === Number(match.params.jobId)
    }) || {}
  }

  getField (): Asset {
    const job = this.getJob()
    const { fields } = this.props
    return job
      ? _find(fields, (field: Asset): boolean => {
        return field[PROP_ASSET_ID] === job[PROP_FIELD]
      })
      : null
  }

  getFarm (): Asset {
    const job = this.getJob()
    const { farms } = this.props
    return job
      ? _find(farms, (farm: Asset): boolean => {
        return farm[PROP_ASSET_ID] === job[PROP_FARM]
      })
      : null
  }

  render (): React.Element<any> {
    const job = this.getJob()
    const field = this.getField()
    const farm = this.getFarm()
    return (
      <div
        data-test='hfty-recommendations-dom/RecommendationsManager'
        style={styles.container}
      >
        <div style={styles.header}>
          <Link to={`/`}>
            <Button
              raised
              dense
              primary
            >
              Back
            </Button>
          </Link>
          <div style={styles.titleWrapper}>
            <span style={styles.heading}>
              <FormattedMessage {...recommendation_messages['AddRecommendation.recommendations']} />
            </span>
            <span style={styles.headingItem}>
              <span style={styles.itemTitle}>Job Id:</span>
              {job ? job[PROP_SOIL_JOB_ID] : '--'}
            </span>
            <span style={styles.headingItem}>
              <span style={styles.itemTitle}>Farm:</span>
              {farm ? farm[PROP_LABEL] : '--'}
            </span>
            <span style={styles.headingItem}>
              <span style={styles.itemTitle}>Field:</span>
              {field ? field[PROP_LABEL] : '--'}
            </span>
          </div>
        </div>
        {job[PROP_STATUS] === RESULTS_POSTED
          ? <div style={styles.datatableContainer}>
            <DataTable
              token={this.props.token}
              routerQuery={this.props.routerQuery}
            >
              <div
                style={styles.tableButton}
                onClick={this.onAdd}
              >
                <Add color={this.canAdd() ? dark() : lightGrey()} />
              </div>
              <div
                style={styles.tableButton}
                onClick={this.onEdit}
              >
                <Pencil color={this.canEdit() ? dark() : lightGrey()} />
              </div>
              <div
                style={styles.tableButton}
                onClick={this.onDelete}
              >
                <Delete color={this.canDelete() ? dark() : lightGrey()} />
              </div>
            </DataTable>
            <DeleteRecommendations
              crops={this.props.crops}
              operations={this.props.operations}
              onSubmit={this.props.actions.onSubmitOperations}
              onCancel={this.props.actions.onCancelOperations}
              currentUserUnits={this.props.currentUserUnits}
            />
          </div>
          : <div style={styles.notAvailable}>
            <FormattedMessage {...recommendation_messages['AddRecommendation.recommendationNotAvailable']} />
          </div>
        }

      </div>
    )
  }
}

export default withRouter(recommendationsManagerContainer(RecommendationsManager))
